import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { deleteData, getAllOrganisationData } from '../utils/api/organisationAPI';
import { toast } from 'sonner';
import ConfirmDeleteModal from './child/ConfirmDeleteModal';
import { motion } from 'framer-motion';

const OrganisationListLayer = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [organisationToDelete, setOrganisationToDelete] = useState(null);
    const navigate = useNavigate();

    // function on click on view button 
    const handleViewClick = (id) => {
        navigate(`/organisation/view/${id}`);
    };

    // function on click on edit button 
    const handleEditClick = (id) => {
        navigate(`/organisation/edit/${id}`);
    };

    useEffect(() => {
        async function fetchData() {
            // Tell the shimmer to show
            setIsLoading(true);
            try {
                const response = await getAllOrganisationData({ setIsLoading });
                setData(response?.organizations || []);
            } catch (e) {
                console.error(e);
            } finally {
                setIsLoading(false);
            }
        }
        fetchData();
    }, [organisationToDelete]);

    // Handle the delete button click
    const handleDeleteClick = (organisationId) => {
        setOrganisationToDelete(organisationId);
        setShowModal(true);
    };

    const handleConfirmDelete = async () => {
        try {
            const response = await deleteData(organisationToDelete);
            toast.success(response?.message || "Organisation deleted successfully.");
            setShowModal(false);
            setOrganisationToDelete(null);
        } catch (error) {
            toast.error(error.message);
            console.error("Delete error:", error.message);
            setShowModal(false);
        }
    };

    // Handle the cancel delete action
    const handleCancelDelete = () => {
        setShowModal(false);
    };

    return (
        <motion.div 
        initial={{
            opacity: 0
        }}
        animate={{
            opacity: 1
        }}
        transition={{
            duration: 1
        }}
        className="card h-100 p-0 radius-12">
            
            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col"className="text-center" > 
                                        ID
                                </th>
                                <th scope="col" className="text-center">Org Name</th>
                                <th scope="col">Minute Used</th>
                                <th scope="col" className="text-center">Sector</th>
                                <th scope="col">Subscription</th>
                                <th scope="col" className="text-center">
                                    Status
                                </th>
                                <th scope="col" className="text-center">
                                    Action
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {isLoading ? (
                                // ─── SHIMMER ROWS WHEN LOADING ─────────────────────────────────────────
                                Array.from({ length: 5 }).map((_, idx) => (
                                    <tr key={`shimmer-row-${idx}`}>
                                        <td>
                                            <div className="shimmer shimmer-id" />
                                        </td>
                                        <td>
                                            <div className="shimmer shimmer-text" />
                                        </td>
                                        <td>
                                            <div className="shimmer shimmer-text" />
                                        </td>
                                        <td>
                                            <div className="shimmer shimmer-text" />
                                        </td>
                                        <td >
                                            <div className="shimmer shimmer-badge" />
                                        </td>
                                        <td className="text-center">
                                            <div className="shimmer shimmer-badge" />
                                        </td>
                                        <td className="text-center">
                                            <div className="shimmer shimmer-actions" />
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                // ─── ACTUAL DATA ROWS ──────────────────────────────────────────────────
                                data.map((item) => (
                                    <tr key={item.id}>
                                        <td>
                                            <div className="d-flex align-items-center gap-10">
                                                {item.id}
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {item.name}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <span className="text-md mb-0 fw-normal text-secondary-light">
                                                {item.minutesUsed}
                                            </span>
                                        </td>
                                        <td className="text-center">{item.sector == null ? "-" : item.sector}</td>
                                        <td>
                                            {item.isSubscribed ? (
                                                <span className="badge text-sm fw-semibold text-success-600 bg-success-100 px-20 py-9 radius-4 text-white">
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="badge text-sm fw-semibold text-danger-600 bg-danger-100 px-20 py-9 radius-4 text-white">
                                                    InActive
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            {item.status ? (
                                                <span className="badge text-sm fw-semibold text-success-600 bg-success-100 px-20 py-9 radius-4 text-white">
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="badge text-sm fw-semibold text-danger-600 bg-danger-100 px-20 py-9 radius-4 text-white">
                                                    InActive
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex align-items-center gap-10 justify-content-center">
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewClick(item.id)}
                                                    className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                >
                                                    <Icon
                                                        icon="majesticons:eye-line"
                                                        className="icon text-xl"
                                                    />
                                                </button>
                                                {/* <button
                                                    type="button"
                                                    onClick={() => handleEditClick(item.id)}
                                                    className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                >
                                                    <Icon icon="lucide:edit" className="menu-icon" />
                                                </button> */}
                                                {/* <button
                                                    type="button"
                                                    className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    onClick={() => handleDeleteClick(item.id)}
                                                >
                                                    <Icon
                                                        icon="fluent:delete-24-regular"
                                                        className="menu-icon"
                                                    />
                                                </button> */}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Modal for Delete Confirmation */}
                {showModal && (
                    <ConfirmDeleteModal
                        handleConfirmDelete={handleConfirmDelete}
                        handleCancelDelete={handleCancelDelete}
                    />
                )}

                {/* <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                    <span>Showing 1 to {data.length <= 10 ? data.length : 10} of {data.length} entries</span>
                </div> */}
            </div>
        </motion.div>
    );
};

export default OrganisationListLayer;
